import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { db, auth } from './firebase';
import { AppUser, WhitelistEntry, UserRole, UserPermissions, RegistrationData } from '../types';

const USERS_COLLECTION = 'users';
const WHITELIST_COLLECTION = 'whitelist';

// Owner account configuration
const OWNER_EMAIL = 'jon@lemon.no';

export const userService = {
  // Initialize default whitelist (run once)
  async initializeDefaultWhitelist(): Promise<void> {
    try {
      // Check if whitelist already exists
      const whitelistSnapshot = await getDocs(collection(db, WHITELIST_COLLECTION));
      if (!whitelistSnapshot.empty) {
        return; // Whitelist already initialized
      }

      const defaultEntries: Omit<WhitelistEntry, 'id' | 'createdAt'>[] = [
        {
          type: 'domain',
          value: '@lemon.no',
          defaultRole: 'user', // Fixed: all users default to 'user' role
          createdBy: 'system',
          isActive: true
        },
        {
          type: 'domain', 
          value: '@eve.tech',
          defaultRole: 'user',
          createdBy: 'system',
          isActive: true
        },
        {
          type: 'domain',
          value: '@marketer.tech', 
          defaultRole: 'user',
          createdBy: 'system',
          isActive: true
        }
      ];

      // Add default whitelist entries
      for (const entry of defaultEntries) {
        await addDoc(collection(db, WHITELIST_COLLECTION), {
          ...entry,
          createdAt: Timestamp.now()
        });
      }

      console.log('Default whitelist initialized');
    } catch (error) {
      console.error('Error initializing whitelist:', error);
      throw error;
    }
  },

  // Check if email is whitelisted
  async isEmailWhitelisted(email: string): Promise<{ allowed: boolean; defaultRole?: UserRole }> {
    try {
      const whitelistSnapshot = await getDocs(
        query(collection(db, WHITELIST_COLLECTION), where('isActive', '==', true))
      );
      
      const emailLower = email.toLowerCase();
      
      for (const doc of whitelistSnapshot.docs) {
        const entry = doc.data() as WhitelistEntry;
        
        if (entry.type === 'email' && entry.value.toLowerCase() === emailLower) {
          return { allowed: true, defaultRole: entry.defaultRole };
        }
        
        if (entry.type === 'domain' && emailLower.endsWith(entry.value.toLowerCase())) {
          return { allowed: true, defaultRole: entry.defaultRole };
        }
      }
      
      return { allowed: false };
    } catch (error) {
      console.error('Error checking whitelist:', error);
      return { allowed: false };
    }
  },

  // Register new user
  async registerUser(data: RegistrationData): Promise<AppUser> {
    try {
      // Initialize whitelist if this is the first user
      await this.initializeDefaultWhitelist();

      // Check whitelist
      const whitelistCheck = await this.isEmailWhitelisted(data.email);
      if (!whitelistCheck.allowed) {
        throw new Error(`Registration not allowed. Your email domain is not authorized. Please contact the administrator if you believe this is an error.`);
      }

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
      const firebaseUser = userCredential.user;

// Send email verification with continue URL back to app
await sendEmailVerification(firebaseUser, {
  url: 'https://gulesider.lemon.no',
  handleCodeInApp: false
});

      // Determine role - Owner gets owner role, everyone else gets user
      let role: UserRole = 'user';
      if (data.email.toLowerCase() === OWNER_EMAIL.toLowerCase()) {
        role = 'owner';
      }

      // Create app user record
      const appUserData: Omit<AppUser, 'id'> = {
        email: data.email.toLowerCase(),
        displayName: data.displayName,
        role,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: data.email.toLowerCase() // Self-registration
      };

      // Try to link to existing Person record
      const personQuery = query(
        collection(db, 'people'), 
        where('email', '==', data.email.toLowerCase())
      );
      const personSnapshot = await getDocs(personQuery);
      
      if (!personSnapshot.empty) {
        appUserData.personId = personSnapshot.docs[0].id;
      }

      // Save to Firestore
      const docRef = await addDoc(collection(db, USERS_COLLECTION), {
        ...appUserData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });

      return {
        id: docRef.id,
        ...appUserData
      };
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'Registration failed');
    }
  },

  // Get user by email
  async getUserByEmail(email: string): Promise<AppUser | null> {
    try {
      const q = query(
        collection(db, USERS_COLLECTION), 
        where('email', '==', email.toLowerCase())
      );
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: doc.data().lastLoginAt?.toDate() || undefined,
      } as AppUser;
    } catch (error) {
      console.error('Error fetching user by email:', error);
      throw error;
    }
  },

  // Update user's last login
  async updateLastLogin(userId: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        lastLoginAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating last login:', error);
      // Don't throw error for login timestamp update
    }
  },

  // Get user permissions based on role
  getUserPermissions(role: UserRole): UserPermissions {
    switch (role) {
      case 'owner':
        return {
          canViewUsers: true,
          canManageUsers: true,
          canManageWhitelist: true,
          canManageContent: true,
          canClaimAnyContent: true,
          canUploadContent: true,
          canEditOwnContent: true,
          canEditAnyProfile: true,
          canDeleteContent: true
        };
      
      case 'admin':
        return {
          canViewUsers: true,
          canManageUsers: true,
          canManageWhitelist: false, // Only owner can manage whitelist
          canManageContent: true,
          canClaimAnyContent: true,
          canUploadContent: true,
          canEditOwnContent: true,
          canEditAnyProfile: true,
          canDeleteContent: true
        };
      
      case 'user':
        return {
          canViewUsers: false,
          canManageUsers: false,
          canManageWhitelist: false,
          canManageContent: false,
          canClaimAnyContent: false,
          canUploadContent: true,
          canEditOwnContent: true,
          canEditAnyProfile: false, // Can only edit own profile
          canDeleteContent: false   // Can only delete own content
        };
      
      case 'view-only':
        return {
          canViewUsers: false,
          canManageUsers: false,
          canManageWhitelist: false,
          canManageContent: false,
          canClaimAnyContent: false,
          canUploadContent: false,
          canEditOwnContent: false,
          canEditAnyProfile: false,
          canDeleteContent: false
        };
      
      default:
        return {
          canViewUsers: false,
          canManageUsers: false,
          canManageWhitelist: false,
          canManageContent: false,
          canClaimAnyContent: false,
          canUploadContent: false,
          canEditOwnContent: false,
          canEditAnyProfile: false,
          canDeleteContent: false
        };
    }
  },

  // Get all users (admin/owner only)
  async getAllUsers(): Promise<AppUser[]> {
    try {
      const q = query(collection(db, USERS_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        lastLoginAt: doc.data().lastLoginAt?.toDate() || undefined,
      })) as AppUser[];
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  },

  // Update user role (admin/owner only)
  async updateUserRole(userId: string, newRole: UserRole, updatedBy: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  // Deactivate user (admin/owner only)
  async deactivateUser(userId: string, updatedBy: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        isActive: false,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  },

  // Reactivate user (admin/owner only)
  async reactivateUser(userId: string, updatedBy: string): Promise<void> {
    try {
      const userRef = doc(db, USERS_COLLECTION, userId);
      await updateDoc(userRef, {
        isActive: true,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error reactivating user:', error);
      throw error;
    }
  },

  // Get all whitelist entries (admin/owner only)
  async getWhitelistEntries(): Promise<WhitelistEntry[]> {
    try {
      const q = query(collection(db, WHITELIST_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as WhitelistEntry[];
    } catch (error) {
      console.error('Error fetching whitelist entries:', error);
      throw error;
    }
  },

  // Add whitelist entry (owner only)
  async addWhitelistEntry(
    type: 'domain' | 'email', 
    value: string, 
    defaultRole: UserRole, 
    createdBy: string
  ): Promise<void> {
    try {
      await addDoc(collection(db, WHITELIST_COLLECTION), {
        type,
        value: value.toLowerCase(),
        defaultRole,
        createdBy,
        isActive: true,
        createdAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding whitelist entry:', error);
      throw error;
    }
  },

  // Check if user's email is verified
  isEmailVerified(firebaseUser: any): boolean {
    return firebaseUser?.emailVerified || false;
  },

  // Resend verification email
  async resendVerificationEmail(firebaseUser: any): Promise<void> {
    try {
      await sendEmailVerification(firebaseUser);
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  },

  // Toggle whitelist entry active status (owner only)
  async toggleWhitelistEntry(entryId: string): Promise<void> {
    try {
      const entryRef = doc(db, WHITELIST_COLLECTION, entryId);
      const entryDoc = await getDoc(entryRef);
      
      if (entryDoc.exists()) {
        const currentStatus = entryDoc.data().isActive;
        await updateDoc(entryRef, {
          isActive: !currentStatus
        });
      }
    } catch (error) {
      console.error('Error toggling whitelist entry:', error);
      throw error;
    }
  }
};